/**
 * Report copy — layer 4 of 4. Every sentence in the document, with the
 * condition that fires it.
 *
 * THE ONE RULE THIS FILE ENFORCES: no arithmetic. Not a `+`, not a `/`, not a
 * threshold comparison against a raw figure. Every number and every boolean
 * arrives pre-computed from derive.ts, and every shape of a number comes from
 * format.ts. That is what lets a compliance or product reviewer open this file
 * and actually read it, and it stops the same threshold being written two
 * different ways in two different sentences.
 *
 * Copy that must never be generated (spec §11.4):
 *   • no sentence asserts a guarantee — "should be worth, on these
 *     assumptions", never "will be worth"
 *   • no sentence recommends buying a specific security
 *   • assumptions are named as assumptions wherever a number depends on them
 *   • every tax statement carries its "confirm with your adviser" line in the
 *     same section, not fifteen pages later
 */

import type { Block, Fragment } from "./blocks";
import { THRESHOLDS } from "./derive";
import type { GoalFacts, LiabilityFacts, PlanFacts } from "./derive";
import {
  capitalise,
  dateLong,
  gapInWords,
  list,
  money,
  moneyExact,
  monthYear,
  percent,
  percentWhole,
  periodNoun,
  schemeName,
  points,
  pluralWords,
  s,
  tenureLong,
  titleCase,
  words,
} from "./format";

const p = (...fragments: Fragment[]): Fragment[] => fragments;

/* ══════════════════════════ the two openers ═══════════════════════════ */

/**
 * The cover subtitle. Hardest sentence in the document, because it describes
 * the plan's shape rather than its contents: the number word, the plural, the
 * noun, the conjunction position, and whether "reviewed together" makes sense
 * at all — it does not when there is only one thing to review.
 */
export const coverSubtitle = (facts: PlanFacts): string => {
  const parts: string[] = [];
  if (facts.goalCount) {
    parts.push(pluralWords(facts.goalCount, "savings goal", "savings goals"));
  }
  if (facts.liabilityCount) {
    // The specific noun only when every loan is the same type.
    parts.push(
      `${words(facts.liabilityCount)} ${facts.liabilityNoun}${s(facts.liabilityCount)}`
    );
  }
  if (facts.hasSwp) parts.push("a retirement drawdown");

  if (parts.length > 1) return `${capitalise(list(parts))}, reviewed together`;

  if (facts.goalCount === 1) return "A funding plan for one savings goal";
  if (facts.goalCount > 1) return `${capitalise(parts[0])}, reviewed together`;
  if (facts.liabilityCount === 1) return `A review of your ${facts.liabilityNoun}`;
  if (facts.liabilityCount > 1) return `${capitalise(parts[0])}, reviewed together`;
  if (facts.hasSwp) return "A review of your retirement income";
  return "";
};

/** Names the parts, then delivers a verdict on affordability. */
export const glanceOpening = (facts: PlanFacts): Block => {
  /* Naming every item stops working past two goals — collapse to a count. */
  const named: string[] = [];
  if (facts.goalCount === 1) named.push(facts.goals[0].phrase);
  else if (facts.goalCount === 2) {
    named.push(facts.goals[0].phrase, facts.goals[1].phrase);
  } else if (facts.goalCount > 2) {
    named.push(pluralWords(facts.goalCount, "savings goal", "savings goals"));
  }
  if (facts.liabilityCount === 1) named.push(facts.liabilities[0].phrase);
  else if (facts.liabilityCount > 1) {
    named.push(pluralWords(facts.liabilityCount, "loan", "loans"));
  }
  if (facts.hasSwp) {
    // Distinct from a Retirement *goal*, which may already be named above —
    // "your retirement and your retirement income" reads as a duplication.
    named.push(
      facts.swp?.startDate
        ? `the income you expect to draw from ${monthYear(facts.swp.startDate)}`
        : "the income you expect to draw"
    );
  }

  const naming = `This plan covers ${pluralWords(facts.partCount, "thing", "things")}: ${list(named)}.`;

  /* Agreement. With a single part "together they ask" is wrong on both counts —
     spec §5.2 lists it/them and is/are among the words that must agree. */
  const solo = facts.partCount === 1;
  const them = solo ? "it" : "them";
  const theyAsk = solo ? "It asks" : "Together they ask";
  const theyFit = solo ? "It fits" : "Together they fit";
  const theyUse = solo ? "It uses" : "Together they use";

  /* The verdict, and the tail it is only sometimes licensed to carry. */
  let verdict: string;
  switch (facts.affordability) {
    case "unrecorded":
      verdict = `No monthly surplus is recorded against ${them}, which is the first thing to resolve.`;
      break;
    case "short":
      verdict = `${theyAsk} for more than your recorded surplus, by ${money(facts.shortfall)} a month.`;
      break;
    case "tight":
      verdict = `${theyFit} inside your monthly surplus, with a little to spare.`;
      break;
    default: {
      if (!facts.requiresMonthly) {
        verdict = `${solo ? "It is" : "They are"} met in full by the one-time capital, so nothing is asked of your monthly surplus at all — the whole of ${money(facts.surplus)} a month stays uncommitted.`;
        break;
      }
      // "The most important decision" is licensed by the drawdown failing, not
      // by there being spare cash. Gate it on what actually justifies it.
      const tail = facts.swp?.exhausted
        ? ", and what you do with the rest is the most important decision in this document"
        : `, leaving ${money(facts.spare)} a month uncommitted`;
      verdict = `${theyUse} ${percentWhole(facts.committedSharePct)} of your monthly surplus${tail}.`;
    }
  }

  return {
    id: "glance.opening",
    section: "glance",
    kind: "lead",
    tone: facts.affordability === "short" ? "caution" : "neutral",
    subject: "plan",
    body: [p(naming), p(verdict)],
  };
};

/* ════════════════════════════════ goals ══════════════════════════════ */

const goalBlocks = (goal: GoalFacts, facts: PlanFacts): Block[] => {
  const out: Block[] = [];
  const subject = {
    subject: "goal" as const,
    subjectId: goal.id,
    subjectName: goal.label,
  };

  /* Horizon overrides appetite. Silent when the two agree — an explanation
     nobody needed is noise. Each sentence is gated on its own so a trailing
     clause can vanish without taking the paragraph with it. */
  if (goal.riskMismatch === "debt-for-aggressive") {
    out.push({
      id: "goal.riskMismatch",
      section: "goal",
      kind: "callout",
      tone: "neutral",
      explanatory: true,
      ...subject,
      title: `Why a debt plan when your risk profile is ${facts.riskProfile.toLowerCase()}`,
      body: [
        p("Horizon overrides appetite here."),
        p(
          `${capitalise(goal.phrase)} is needed in ${tenureLong(goal.years, goal.months)}, which is not long enough for equity to recover from a bad year. The money sits in ${goal.segment.toLowerCase()} for that reason alone, not because of how you invest elsewhere.`
        ),
        ...(facts.liabilityCount
          ? [
              p(
                "The loan offset runs for longer and is invested differently for exactly this reason — see ",
                { ref: "liability" },
                "."
              ),
            ]
          : []),
      ],
    });
  }
  if (goal.riskMismatch === "equity-for-conservative") {
    out.push({
      id: "goal.riskMismatch",
      section: "goal",
      kind: "callout",
      tone: "neutral",
      explanatory: true,
      ...subject,
      title: "Why equity when your risk profile is conservative",
      body: [
        p("Horizon overrides appetite here."),
        p(
          `${tenureLong(goal.years, goal.months)} is long enough that inflation, not volatility, is the larger risk to ${goal.phrase}. A conservative allocation over that period is likely to fall behind the cost it is meant to meet.`
        ),
      ],
    });
  }

  if (goal.isVeryNear) {
    out.push({
      id: "goal.veryNear",
      section: "goal",
      kind: "callout",
      tone: "caution",
      summary: true,
      ...subject,
      title: "This goal is very close",
      body: [
        p(
          `${capitalise(goal.phrase)} falls due in ${tenureLong(goal.years, goal.months)}. Almost nothing compounds over that horizon, so the instalment is close to the full target divided by the months available — returns barely move it either way.`
        ),
      ],
    });
  }

  if (goal.inflationBlunt) {
    out.push({
      id: "goal.inflationBlunt",
      section: "goal",
      kind: "table-note",
      tone: "neutral",
      ...subject,
      body: [
        p(
          `Inflation is the default ${percent(goal.inflationPct)}, which lifts the cost by ${percentWhole(goal.upliftPct)} by ${monthYear(goal.goalDate)}. For a discretionary goal that assumption is blunt — the price of a trip or a vehicle moves with the market you buy it in, not with a general index. Worth setting your own figure.`
        ),
      ],
    });
  }

  if (goal.inflationMayBeLow) {
    out.push({
      id: "goal.inflationLow",
      section: "goal",
      kind: "table-note",
      tone: "caution",
      ...subject,
      body: [
        p(
          `Inflation is the default ${percent(goal.inflationPct)}. Education and medical costs have historically run well above general inflation, so this figure may understate what ${goal.phrase} costs on the day. Worth setting your own.`
        ),
      ],
    });
  }

  if (goal.fullyCovered) {
    out.push({
      id: "goal.fullyCovered",
      section: "gap",
      kind: "callout",
      tone: "good",
      summary: true,
      ...subject,
      title: "The one-time capital already covers this goal",
      body: [
        p(
          `${capitalise(goal.phrase)} needs ${money(goal.futureCost)} on the target date, and the lumpsum route is projected to deliver ${money(goal.coveredByLumpsum)}. No monthly instalment is required for it.`
        ),
        p(
          "Worth checking the goal is sized correctly before treating the surplus as free — an amount this far above the target usually means the cost was entered low, or that capital could be doing more elsewhere."
        ),
      ],
    });
  }

  /* ── the goal has no lumpsum behind it ── */
  if (goal.bondState === "not-this-goal") {
    out.push({
      id: "goal.sipOnly",
      section: "gap",
      kind: "table-note",
      tone: "neutral",
      ...subject,
      body: [
        p(
          `There is no lumpsum allocated to this goal, so the full ${money(goal.futureCost)} comes from the monthly instalment.`
        ),
      ],
    });
  }

  out.push(...bondBlocks(goal, facts));
  out.push(...fundBlocks(goal, facts));
  return out;
};

/* ═══════════════════════════ bond and deployment ═════════════════════ */

const bondBlocks = (goal: GoalFacts, facts: PlanFacts): Block[] => {
  const out: Block[] = [];
  const subject = {
    subject: "goal" as const,
    subjectId: goal.id,
    subjectName: goal.label,
  };

  if (goal.bondState === "none-eligible") {
    out.push({
      id: "bond.noneEligible",
      section: "deployment",
      kind: "callout",
      tone: "caution",
      summary: true,
      ...subject,
      title: "No bond could be used for this goal",
      body: [
        p(
          goal.bondUnavailableDetail ??
            "No bond in the inventory matched this goal's date window at the amount available."
        ),
        p("The lumpsum is deployed through mutual funds instead."),
      ],
    });
    return out;
  }

  const bond = goal.bond;
  if (!bond) return out;

  if (goal.bondState === "one-eligible") {
    out.push({
      id: "bond.onlyOneEligible",
      section: "deployment",
      kind: "table-note",
      tone: "neutral",
      ...subject,
      body: [
        p(
          `Only one bond qualified. The screen looks for paper maturing in the six months before your goal date and up to a fortnight after it, priced so the amount available buys at least one lot — so this was selected without competition rather than as the best of several.`
        ),
      ],
    });
  } else {
    out.push({
      id: "bond.bestOfSeveral",
      section: "deployment",
      kind: "table-note",
      tone: "neutral",
      ...subject,
      body: [
        p(
          `Best of ${words(goal.eligibleBondCount)} that matched your goal date and the amount available, ranked on yield first, then how closely it matures to the date you need the money.`
        ),
      ],
    });
  }

  if (bond.pricedAtPar) {
    out.push({
      id: "bond.pricedAtPar",
      section: "deployment",
      kind: "callout",
      tone: "caution",
      summary: true,
      ...subject,
      title: "This bond is priced at par, not at a live quote",
      body: [
        p(
          "No live price was available, so it is valued at par and the yield above follows from that assumption rather than from the market. Confirm the price on the trade floor before acting — every figure downstream of it moves with it."
        ),
      ],
    });
  }

  if (bond.accruedPresent && !bond.accruedNegative) {
    out.push({
      id: "bond.accrued",
      section: "deployment",
      kind: "table-note",
      tone: "neutral",
      explanatory: true,
      ...subject,
      body: [
        p(
          `The settlement amount is more than the price of the bond itself. Interest has been building since the last coupon date and belongs to the seller, so ${moneyExact(bond.accruedInterest)} of accrued interest is added — you get it back with the next coupon.`
        ),
      ],
    });
  }

  if (bond.accruedNegative || bond.inShutPeriod) {
    out.push({
      id: "bond.shutPeriod",
      section: "deployment",
      kind: "callout",
      tone: "neutral",
      explanatory: true,
      ...subject,
      title: "You settle for less than the price of the bond",
      body: [
        p(
          `This bond is inside its shut period, so the coming coupon is paid to the seller rather than to you. The accrued interest is therefore negative — ${moneyExact(bond.accruedInterest)} — and the settlement amount of ${moneyExact(bond.settlementAmount)} is below the clean price.`
        ),
      ],
    });
  }

  if (bond.atDiscount) {
    out.push({
      id: "bond.discount",
      section: "deployment",
      kind: "table-note",
      tone: "good",
      ...subject,
      body: [
        p(
          `Bought below face value, which is why the yield of ${percent(bond.ytm, 2)} sits above the ${percent(bond.couponPct, 2)} coupon: the difference is repaid at maturity as well as through the coupons.`
        ),
      ],
    });
  } else if (bond.atPremium) {
    out.push({
      id: "bond.premium",
      section: "deployment",
      kind: "table-note",
      tone: "neutral",
      ...subject,
      body: [
        p(
          `Bought above face value. Only the face value is repaid at maturity, so the effective yield of ${percent(bond.ytm, 2)} is below the ${percent(bond.couponPct, 2)} coupon — the premium is recovered through the coupons, not at the end.`
        ),
      ],
    });
  }

  if (bond.idleIsLotArtifact) {
    out.push({
      id: "bond.idleLotArtifact",
      section: "deployment",
      kind: "table-note",
      tone: "neutral",
      explanatory: true,
      ...subject,
      body: [
        p(
          `${moneyExact(bond.idle)} is left uninvested. Bonds trade in fixed lots and this was not enough for one more, so it is deployed into the same goal as a one-time mutual-fund investment rather than left in cash. This is a lot-size artifact, not an error.`
        ),
      ],
    });
  } else if (bond.idleIsLarge) {
    out.push({
      id: "bond.idleLarge",
      section: "deployment",
      kind: "callout",
      tone: "caution",
      summary: true,
      ...subject,
      title: `${moneyExact(bond.idle)} of your lumpsum could not go into the bond`,
      body: [
        p(
          `That is ${percentWhole(bond.idlePctOfLumpsum)} of the amount you set aside, left over because bonds trade in fixed lots. A slightly different amount would buy a cleaner multiple and put more of it to work at the bond's yield.`
        ),
      ],
    });
  }

  if (bond.maturesBeforeGoal) {
    out.push({
      id: "bond.maturesBeforeGoal",
      section: "deployment",
      kind: "callout",
      tone: "good",
      summary: true,
      ...subject,
      title: "The bond matures before you need the money",
      body: [
        p(
          `It repays ${gapInWords(bond.gapDays)} before ${goal.phrase} falls due, so the proceeds are in hand ahead of the date rather than after it.`
        ),
        ...(bond.parkingFundName
          ? [
              p(
                `The principal is parked in ${titleCase(bond.parkingFundName)} for that gap rather than sitting in cash.`
              ),
            ]
          : []),
      ],
    });
  } else if (bond.gapValid) {
    out.push({
      id: "bond.maturesAfterGoal",
      section: "deployment",
      kind: "callout",
      tone: "caution",
      summary: true,
      ...subject,
      title: `This bond matures ${gapInWords(bond.gapDays)} after your goal date`,
      body: [
        p(
          `${capitalise(goal.phrase)} falls due on ${dateLong(goal.goalDate)}, and the bond repays on ${dateLong(bond.maturity)}. The figures in this section are calculated to the maturity date, so the money is not actually in hand on the day you need it.`
        ),
        p(
          "Either the goal date has a fortnight of give in it, or this bond is the wrong instrument for it. Worth deciding which before the order is placed."
        ),
      ],
    });
  }

  /*
   * A footnote, not a panel. What the yield compensates for is background a
   * reader wants available, not a finding they must act on — and boxed it took
   * a third of the deployment page away from the two findings that ARE
   * actionable. Being explanatory it is exempt from the callout budget, so as a
   * panel it could never be demoted to make room either.
   */
  out.push({
    id: "bond.credit",
    section: "deployment",
    kind: "table-note",
    tone: "neutral",
    explanatory: true,
    ...subject,
    body: [
      p(
        bond.rating
          ? `This bond is rated ${bond.rating}. A coupon of ${percent(bond.couponPct, 2)} is higher than government paper of the same tenure pays, and that difference is compensation for the chance the issuer does not repay — which would affect the coupons and the principal alike.`
          : `No rating was returned for this bond. Its ${percent(bond.couponPct, 2)} coupon still carries issuer risk, affecting the coupons and the principal alike, and without a rating there is nothing here to size that risk against. Worth confirming before the order is placed.`
      ),
    ],
  });

  if (bond.ytmDiverges) {
    out.push({
      id: "bond.ytmDiverges",
      section: "deployment",
      kind: "table-note",
      tone: "neutral",
      ...subject,
      body: [
        p(
          `The quoted yield and our own calculation differ slightly — ${percent(bond.ytm, 2)} against ${percent(bond.ytmComputed, 2)}. The quoted figure is used throughout; the difference comes from day-count conventions and does not change the cashflows.`
        ),
      ],
    });
  }

  void facts;
  return out;
};

/* ══════════════════════════════ funds ════════════════════════════════ */

const fundBlocks = (goal: GoalFacts, facts: PlanFacts): Block[] => {
  const out: Block[] = [];
  const subject = {
    subject: "goal" as const,
    subjectId: goal.id,
    subjectName: goal.label,
  };
  const funds = goal.funds;

  if (funds.allUnscored) {
    out.push({
      id: "funds.noneScored",
      section: "goal-funds",
      kind: "callout",
      tone: "caution",
      summary: true,
      ...subject,
      title: "These funds are placeholders, not recommendations",
      body: [
        p(
          "None of them carries a SMEST score, so they were chosen on category alone. Treat this allocation as pending a scored alternative rather than as a selection."
        ),
      ],
    });
  } else if (funds.anyScored) {
    out.push({
      id: "funds.scored",
      section: "goal-funds",
      kind: "table-note",
      tone: "good",
      explanatory: true,
      ...subject,
      body: [
        p(
          "Scores run from zero to ten and are built from four things: return above the peer benchmark, volatility relative to peers, the fund's own volatility, and its expense ratio. Where a fund shows no score, there was not enough history to compute one."
        ),
      ],
    });
  }

  if (!funds.capSatisfied) {
    const title =
      funds.concentration === "single-fund"
        ? "Everything sits in one fund"
        : funds.concentration === "single-house"
          ? `Every fund here is from ${titleCase(funds.topAmc ?? "one house")}`
          : `${titleCase(funds.topAmc ?? "One fund house")} carries more than the limit`;
    out.push({
      id: "funds.concentration",
      section: "goal-funds",
      kind: "callout",
      tone: "caution",
      summary: true,
      weight: 2,
      ...subject,
      title,
      body: [
        p(
          `No single fund house should hold more than a fifth of a basket. Here ${titleCase(funds.topAmc ?? "one house")} holds ${percentWhole(funds.topAmcWeightPct)}, which is ${points(funds.capExcessPct)} percentage points above that ceiling.`
        ),
        // The reasoning differs by shape: one fund is single-manager risk;
        // several from one roof is the overlap problem. Reusing the same
        // sentence for both puts a claim about "two funds" beside a table
        // showing one.
        p(
          funds.concentration === "single-fund"
            ? "A single fund means a single manager and a single set of decisions. If that one call goes badly there is nothing else in the basket to offset it."
            : "The cap exists because managers in the same house tend to hold the same underlying stocks, so funds from one roof are less diversified than they look."
        ),
      ],
    });
  }

  if (goal.categoryFit === "debt-at-nil") {
    out.push({
      id: "funds.categoryFit",
      section: "goal-funds",
      kind: "table-note",
      tone: "neutral",
      explanatory: true,
      ...subject,
      body: [
        p(
          "Debt funds are the natural fit here. Their gains are taxed at your slab rather than at equity rates, which is usually the argument against them — at a nil bracket that argument does not apply, so you get the stability without the tax cost that normally comes with it."
        ),
      ],
    });
  }

  if (goal.categoryFit === "arbitrage-at-high") {
    out.push({
      id: "funds.categoryFit",
      section: "goal-funds",
      kind: "table-note",
      tone: "neutral",
      explanatory: true,
      ...subject,
      body: [
        p(
          `A plain debt or liquid fund would be the obvious choice for a horizon this short, but its gains are taxed at your slab — ${percent(facts.tax.bracketPct, 0)} — which leaves little after inflation. An arbitrage fund behaves like one while being taxed as equity, which is why it appears here instead.`
        ),
      ],
    });
  }

  if (funds.dividendPlans.length) {
    out.push({
      id: "funds.dividendPlan",
      section: "goal-funds",
      kind: "callout",
      // Over a long horizon a payout plan quietly undoes the compounding the
      // whole plan depends on; over two years it barely matters.
      tone: goal.isVeryNear ? "neutral" : "caution",
      summary: !goal.isVeryNear,
      ...subject,
      title: "One of these is a payout plan",
      body: [
        p(
          `${list(funds.dividendPlans.map((name) => schemeName(name)))} pays income out rather than reinvesting it. Over ${tenureLong(goal.years, goal.months)} that removes the compounding this goal is relying on, and the payouts are taxable as they arrive. A growth plan of the same fund would suit a goal like this better.`
        ),
      ],
    });
  }

  void facts;
  return out;
};

/* ═══════════════════════════ liabilities ═════════════════════════════ */

const liabilityBlocks = (
  liability: LiabilityFacts,
  index: number,
  facts: PlanFacts
): Block[] => {
  const out: Block[] = [];
  const subject = {
    subject: "liability" as const,
    subjectId: index,
    subjectName: liability.label,
  };

  out.push({
    id: "liability.interest",
    section: "liability",
    kind: "lead",
    tone: "neutral",
    ...subject,
    body: [
      p(
        liability.interestIsLarge
          ? `Over ${tenureLong(liability.years, liability.months)} this loan costs ${money(liability.totalInterest)} in interest — almost ${percentWhole(liability.interestSharePct)} of the amount borrowed.`
          : `Over ${tenureLong(liability.years, liability.months)} this loan costs ${money(liability.totalInterest)} in interest.`
      ),
    ],
  });

  out.push({
    id: "liability.segment",
    section: "liability",
    kind: "table-note",
    tone: "neutral",
    explanatory: true,
    ...subject,
    body: [
      p(
        `The offset is invested for ${tenureLong(liability.years, liability.months)}, which puts it in ${liability.segment.toLowerCase()} on the same rule the goals use. The ${percent(liability.assumedReturnPct)} assumption follows from that, and it is an assumption rather than a promise.`
      ),
    ],
  });

  if (liability.elapsedMonths > 0) {
    out.push({
      id: "liability.catchUp",
      section: "liability",
      kind: "callout",
      tone: liability.cancels ? "good" : "caution",
      summary: true,
      ...subject,
      title: "The loan started before this plan, so the SIP includes a catch-up",
      body: [
        p(
          `The loan has already run ${liability.elapsedMonths} months, so about ${money(liability.elapsedInterest)} of interest is estimated to have been paid at the same monthly pace, making ${money(liability.totalInterest)} over the whole loan. Had the tenth-of-EMI SIP of ${money(liability.sip10)} started with the loan it would have grown further by now; an extra ${money(liability.catchUpSip)} a month over the remaining tenure makes that up, for ${money(liability.monthlySip)} a month in all.`
        ),
        p(
          liability.cancels
            ? "On these assumptions that SIP covers the interest over the whole loan."
            : "Even with the catch-up, this SIP falls short of the interest over the whole loan."
        ),
      ],
    });
  } else if (liability.cancels) {
    out.push({
      id: "liability.cancels",
      section: "liability",
      kind: "callout",
      tone: "good",
      summary: true,
      ...subject,
      title: "The rule of thumb already covers this loan",
      body: [
        p(
          `A SIP of ${money(liability.sip10)} a month — a tenth of the EMI — should grow to ${money(liability.sip10FV)} over the remaining tenure on these assumptions, against ${money(liability.totalInterest)} of interest. No uplift is needed.`
        ),
      ],
    });
  } else {
    out.push({
      id: "liability.fallsShort",
      section: "liability",
      kind: "callout",
      tone: "caution",
      summary: true,
      ...subject,
      title: "The tenth-of-EMI rule falls short here",
      body: [
        p(
          `${money(liability.sip10)} a month should reach ${money(liability.sip10FV)}, against ${money(liability.totalInterest)} of interest. Closing the whole of it needs ${money(liability.sipNeeded)} a month instead — ${money(liability.topUp)} more than the rule of thumb.`
        ),
      ],
    });
  }

  if (liability.interestIsLarge) {
    out.push({
      id: "liability.notASaving",
      section: "liability",
      kind: "table-note",
      tone: "neutral",
      ...subject,
      body: [
        p(
          "This offset does not reduce what the lender charges. The EMI is unchanged; the intention is to build a separate pot roughly the size of the interest, so the cost is met from returns rather than avoided."
        ),
      ],
    });
  }

  if (liability.isLongHorizon) {
    out.push({
      id: "liability.longAssumption",
      section: "liability",
      kind: "table-note",
      tone: "caution",
      ...subject,
      body: [
        p(
          `This rests on ${percent(liability.assumedReturnPct)} a year sustained for ${tenureLong(liability.years, liability.months)}. Over that long a period the assumption matters more than the arithmetic, and a shortfall of a point or two compounds into a materially smaller pot.`
        ),
      ],
    });
  }

  if (liability.classificationLooksOdd) {
    out.push({
      id: "liability.classification",
      section: "liability",
      kind: "callout",
      tone: "caution",
      summary: true,
      ...subject,
      title: "This does not look like a car loan",
      body: [
        p(
          `${money(liability.outstanding)} over ${tenureLong(liability.years, liability.months)} is outside the shape of vehicle finance. If it is secured against property it is a different product with different tax treatment — interest on a housing loan can be deductible, and interest on a car loan for personal use is not.`
        ),
        p("Worth confirming the classification before the tax section is relied on."),
      ],
    });
  }

  if (liability.prepayBeatsInvesting) {
    out.push({
      id: "liability.prepay",
      section: "liability",
      kind: "callout",
      tone: "caution",
      summary: true,
      ...subject,
      title: "Prepaying may beat investing here",
      body: [
        p(
          `The loan charges ${percent(liability.ratePct)} and the offset assumes ${percent(liability.assumedReturnPct)}. Paying the loan down is a certain return at the borrowing rate, while the offset is an assumption — when the first is higher than the second, the offset is the weaker of the two.`
        ),
      ],
    });
  } else {
    out.push({
      id: "liability.prepay",
      section: "liability",
      kind: "table-note",
      tone: "neutral",
      ...subject,
      body: [
        p(
          `The loan charges ${percent(liability.ratePct)} against an assumed ${percent(liability.assumedReturnPct)} on the offset. On those figures investing rather than prepaying is the better of the two, though the first is certain and the second is not.`
        ),
      ],
    });
  }

  if (liability.runsIntoRetirement && facts.swp) {
    out.push({
      id: "liability.intoRetirement",
      section: "liability",
      kind: "callout",
      tone: "critical",
      summary: true,
      weight: 3,
      ...subject,
      title: "This EMI is still running when you start drawing an income",
      body: [
        p(
          `The loan has ${pluralWords(liability.overlapMonths, "month", "months")} left to run after ${dateLong(facts.swp.startDate)}, when the drawdown begins. For that period the EMI of ${money(liability.emi)} comes out of a corpus that is already being drawn down, not out of earnings.`
        ),
      ],
    });
  }

  return out;
};

/* ════════════════════════════ retirement ═════════════════════════════ */

const swpBlocks = (facts: PlanFacts): Block[] => {
  const swp = facts.swp;
  if (!swp) return [];
  const out: Block[] = [];
  const subject = { subject: "swp" as const, subjectName: "Retirement income" };

  if (swp.everlasting) {
    out.push({
      id: "swp.everlasting",
      section: "retirement",
      kind: "callout",
      tone: "good",
      summary: true,
      ...subject,
      title: "Your pension covers the withdrawal",
      body: [
        p(
          `Nothing has to come out of the corpus, so it keeps growing rather than being drawn down. The surplus pension of ${money(swp.pensionSurplus)} a month can itself be invested.`
        ),
      ],
    });
  } else if (swp.exhausted) {
    out.push({
      id: "swp.exhausted",
      section: "retirement",
      kind: "callout",
      tone: swp.severity === "critical" ? "critical" : "caution",
      summary: true,
      weight: 4,
      ...subject,
      title: `The corpus runs out after ${tenureLong(swp.lastsWholeYears, swp.lastsMonths)}`,
      body: [
        p(
          `Drawing ${money(swp.net)} a ${periodNoun(swp.frequency)} from ${money(swp.startCorpus)}, rising ${percent(swp.inflationPct)} a year with inflation, empties it after ${tenureLong(swp.lastsWholeYears, swp.lastsMonths)}.`
        ),
        ...(swp.rateValid
          ? [
              p(
                `That is a withdrawal rate of ${percentWhole(swp.withdrawalRatePct)} a year. Sustaining the same income for ${words(25)} years would need about ${money(swp.sustainableCorpus)} — ${money(swp.corpusShortfall)} more than the plan builds.`
              ),
            ]
          : []),
        ...(swp.nothingIsBuildingIt
          ? [
              p(
                "Nothing in this plan is building that corpus: there is no retirement goal contributing to it."
              ),
            ]
          : []),
      ],
    });
  } else {
    out.push({
      id: "swp.sustainable",
      section: "retirement",
      kind: "callout",
      tone: "good",
      summary: true,
      ...subject,
      title: "The corpus sustains the withdrawal",
      body: [
        p(
          `On these assumptions the balance is not exhausted inside the projection, even with the withdrawal rising ${percent(swp.inflationPct)} a year.`
        ),
        p(
          "One caveat worth naming: this assumes returns arrive evenly. A poor first few years while money is being withdrawn does lasting damage that a good run later does not fully repair."
        ),
      ],
    });
  }

  if (swp.exhausted && facts.spare > 0) {
    // Index access rather than Array.prototype.at: this project builds through
    // Create React App with no ES2022 polyfill, and `at` is the one method here
    // that would need one.
    const lastStep = facts.ladder[facts.ladder.length - 1];
    out.push({
      id: "swp.whatSpareBuys",
      section: "what-it-takes",
      kind: "lead",
      tone: "neutral",
      ...subject,
      body: [
        p(
          `You have ${money(facts.spare)} a month uncommitted today, rising to ${money(lastStep?.spare ?? facts.spare)} once the current commitments finish.`
        ),
        p(
          "Invested rather than left idle, that is the single largest lever on the shortfall above — larger than fund selection, and larger than a different bond."
        ),
      ],
    });
  }

  if (swp.exhausted && facts.spare > 0) {
    out.push({
      id: swp.spareClosesGap ? "swp.spareCloses" : "swp.stillNotEnough",
      section: "what-it-takes",
      kind: "callout",
      tone: swp.spareClosesGap ? "good" : "caution",
      summary: true,
      ...subject,
      title: swp.spareClosesGap
        ? "Investing the spare closes the gap"
        : "Even the spare surplus does not close the gap",
      body: [
        p(
          `Invested at ${percent(swp.returnPct)} until the drawdown begins, the uncommitted surplus should be worth about ${money(swp.spareProjected)} on these assumptions — against a shortfall of ${money(swp.corpusShortfall)}.`
        ),
        p(
          swp.spareClosesGap
            ? "That is the single largest lever available, and it needs no change to any goal or to the loan."
            : "It is the largest lever available, but on its own it is not enough. Closing the rest means a later start to the drawdown, a smaller withdrawal, or a larger surplus."
        ),
      ],
    });
  }

  return out;
};

/* ═══════════════════════════════ tax ═════════════════════════════════ */

const taxBlocks = (facts: PlanFacts): Block[] => {
  const out: Block[] = [];
  const tax = facts.tax;
  const subject = { subject: "plan" as const };
  const entity = facts.isHuf ? "As a HUF" : "As an individual";

  if (tax.state === "refundable") {
    out.push({
      id: "tax.refundable",
      section: "tax",
      kind: "callout",
      tone: "good",
      ...subject,
      title: "The tax withheld on your coupons comes back",
      body: [
        p(
          `${percent(tax.tdsPct, 0)} is deducted at source from every bond coupon, but your slab is ${percent(tax.bracketPct, 0)}. ${entity} in that bracket you owe less than is being withheld, so the difference — about ${money(tax.balance)} across this plan — is recoverable as a refund when you file.`
        ),
        p(
          "Form 15G, filed with the issuer, stops it being withheld in the first place and leaves the money in your hands through the year rather than at the end of it."
        ),
        p("Confirm both points with your tax adviser before acting on them."),
      ],
    });
  } else if (tax.state === "neutral") {
    out.push({
      id: "tax.neutral",
      section: "tax",
      kind: "table-note",
      tone: "neutral",
      ...subject,
      body: [
        p(
          `Tax is deducted at ${percent(tax.tdsPct, 0)} on each coupon, which matches your slab, so there is nothing further to pay and nothing to reclaim. Confirm with your tax adviser.`
        ),
      ],
    });
  } else {
    out.push({
      id: "tax.short",
      section: "tax",
      kind: "callout",
      tone: "caution",
      summary: true,
      ...subject,
      title: "Coupon figures in this report are gross of the tax you still owe",
      body: [
        p(
          `${percent(tax.tdsPct, 0)} is withheld at source, but your slab is ${percent(tax.bracketPct, 0)}. The balance — roughly ${money(tax.balance)} across this plan — is payable when you file, and the coupon and sweep figures shown elsewhere do not deduct it.`
        ),
        p("Confirm the treatment with your tax adviser."),
      ],
    });
  }

  if (tax.confirmBracket) {
    out.push({
      id: "tax.confirmBracket",
      section: "tax",
      kind: "callout",
      tone: "caution",
      summary: true,
      ...subject,
      title: "The nil tax bracket looks worth checking",
      body: [
        p(
          "A zero slab is recorded alongside a surplus and a target of this size, which is an unusual combination. The taxation in this section follows from that figure, so if it was not deliberate the conclusions above change."
        ),
      ],
    });
  }

  return out;
};

/* ═════════════════════════════ assembly ══════════════════════════════ */

/** Every block the plan produces, in document order. */

/* ════════════════ the sections that had no findings of their own ═══════
 *
 * Everything below answers a question the sample report answers and this
 * pipeline previously did not: why one goal gets the lumpsum, whether the
 * retirement target and the drawdown agree with each other, what the levers
 * actually are, and what has to be settled before an order is placed.
 *
 * Same contract as every rule above — no arithmetic, only facts already
 * derived, and a title that states the finding rather than naming the topic.
 */

/** Why a single goal takes the whole lumpsum. Framing, not a warning. */
const lumpsumAllocationBlock = (facts: PlanFacts): Block[] => {
  if (facts.goalCount < 2 || facts.lumpsum <= 0) return [];
  const receiving = facts.goals.find((goal) => goal.usedLumpsum);
  if (!receiving) return [];

  return [
    {
      id: "plan.lumpsumToLongest",
      section: "goals-overview",
      kind: "callout",
      tone: "neutral",
      explanatory: true,
      subject: "plan",
      title: "Why only one goal gets the lumpsum",
      body: [
        receiving.bond
          ? p(
              `A bond has to be held to a fixed date, so it can only be matched to one goal. The plan gives it to the longest — here, ${receiving.phrase} — because that is the horizon with room for a bond to run to redemption.`
            )
          : p(
              `A one-time investment compounds for as long as it is left alone, so it goes to the goal with the longest run — here, ${receiving.phrase}. Split across every goal it would spend its best years funding the nearest one.`
            ),
        p(
          `Every other goal is funded from monthly savings alone. That is a deliberate allocation, not a gap.`
        ),
      ],
    },
  ];
};

/**
 * The target and the drawdown, read against each other.
 *
 * The single most consequential thing a plan can contain, and the one a reader
 * is least likely to spot: each half looks reasonable alone. A goal sized to
 * build a corpus, and a drawdown that empties it in three years, only
 * contradict each other when the two are put side by side.
 */
const retirementCoherenceBlocks = (facts: PlanFacts): Block[] => {
  const swp = facts.swp;
  if (!swp || !swp.exhausted || !swp.rateValid) return [];
  const goal = facts.goals.find((candidate) => candidate.type === "Retirement");
  if (!goal) return [];

  const blocks: Block[] = [
    {
      id: "goal.targetIsNotARetirement",
      section: "goal",
      kind: "callout",
      tone: "critical",
      summary: true,
      weight: 6,
      subject: "goal",
      subjectId: goal.id,
      subjectName: goal.label,
      title: `${money(goal.costToday)} is a retirement target, not a retirement`,
      body: [
        p(
          `This goal is sized at ${money(goal.costToday)} in today's money, which grows to ${money(goal.futureCost)} by ${monthYear(goal.goalDate)}. `,
          { ref: "retirement" },
          ` then asks that sum to pay ${money(swp.net)} a ${periodNoun(swp.frequency)} for the rest of your life from age ${goal.ageAtGoal}. It cannot: the corpus is exhausted in ${tenureLong(swp.lastsWholeYears, swp.lastsMonths)}.`
        ),
        p(
          `Before the fund choices or the bond matter at all, the target figure and the intended drawdown need to be brought into line with each other. `,
          { ref: "what-it-takes" },
          ` sets out the size of the mismatch.`
        ),
      ],
    },
  ];

  /* Retiring early is not a fault, but it lengthens the drawdown at the same
     time as it shortens the accumulation, and both work against the corpus. */
  if (goal.ageAtGoal < THRESHOLDS.earlyRetirementAge) {
    blocks.push({
      id: "goal.retiringEarly",
      section: "goal",
      kind: "callout",
      tone: "caution",
      subject: "goal",
      subjectId: goal.id,
      subjectName: goal.label,
      title: `Retiring at ${goal.ageAtGoal} is unusually early, and it lengthens the problem`,
      body: [
        p(
          `A goal date of ${monthYear(goal.goalDate)} puts you at ${goal.ageAtGoal}. A corpus drawn from that age may need to last forty years or more, which is why the required sum is so much larger than for a retirement at sixty.`
        ),
        p(
          `Moving the date later is the single most powerful lever available — it adds years of accumulation and removes years of drawdown at the same time.`
        ),
      ],
    });
  }

  return blocks;
};

/** How much of a goal the lumpsum actually carries. Framing for the gap page. */
const lumpsumShareBlock = (goal: GoalFacts): Block[] => {
  if (!goal.usedLumpsum || goal.sip <= 0 || !goal.coverageValid) return [];
  return [
    {
      id: "goal.lumpsumShare",
      section: "gap",
      kind: "table-note",
      tone: "neutral",
      explanatory: true,
      subject: "goal",
      subjectId: goal.id,
      subjectName: goal.label,
      body: [
        p(
          `The lumpsum covers ${percentWhole(goal.lumpsumSharePct)} of this goal. The rest comes from the monthly instalment, which is the part your surplus has to carry.`
        ),
      ],
    },
  ];
};

/** A call date is an option, not a promise. */
const callDateBlock = (goal: GoalFacts): Block[] => {
  const bond = goal.bond;
  if (!bond?.isCallDate) return [];
  return [
    {
      id: "bond.callNotMaturity",
      section: "deployment",
      kind: "callout",
      tone: "caution",
      summary: true,
      weight: 4,
      subject: "goal",
      subjectId: goal.id,
      subjectName: goal.label,
      title: "This is a call date, not a maturity date — please confirm what that means here",
      body: [
        p(
          `The instrument is dated to a call on ${dateLong(bond.maturity)}, not a final maturity. A call is an option to redeem early, and everything in this plan assumes it is exercised on that date.`
        ),
        p(
          `If the option belongs to the issuer and they choose not to call, the bond runs on and ${money(bond.totalFaceValue)} does not arrive when the goal needs it. If the option is yours, the date is dependable. The plan cannot tell which from the data it was given, so confirm it with the trade desk before the order is placed.`
        ),
      ],
    },
  ];
};

/** The most expensive tax treatment available, hiding inside a category name. */
const debtTaxBlocks = (facts: PlanFacts): Block[] => {
  if (facts.tax.bracketPct < THRESHOLDS.highTaxBracketPct) return [];

  const exposed = [
    ...facts.goals
      .filter((goal) => goal.funds.debtTaxedWeightPct > 0)
      .map((goal) => ({
        section: "goal-funds" as const,
        subjectId: goal.id as number | string,
        subjectName: goal.label,
        weightPct: goal.funds.debtTaxedWeightPct,
      })),
    ...facts.liabilities
      .map((liability, index) => ({
        section: "liability-funds" as const,
        subjectId: index as number | string,
        subjectName: liability.label,
        weightPct: liability.funds.debtTaxedWeightPct,
      }))
      .filter((entry) => entry.weightPct > 0),
  ];
  if (!exposed.length) return [];

  const blocks: Block[] = exposed.map((entry) => ({
    id: "funds.debtTaxed",
    section: entry.section,
    kind: "callout",
    tone: "caution",
    subject: entry.section === "goal-funds" ? "goal" : "liability",
    subjectId: entry.subjectId,
    subjectName: entry.subjectName,
    title: `${percentWhole(entry.weightPct)} of this allocation is taxed as debt, at your ${percentWhole(facts.tax.bracketPct)} slab`,
    body: [
      p(
        `Debt taxation adds the gain to your income and taxes it at slab whatever the holding period. At a ${percentWhole(facts.tax.bracketPct)} bracket that is the most expensive treatment available to you.`
      ),
      p(
        `An arbitrage fund carries comparable risk and gets equity treatment instead. Worth revisiting before the first instalment.`
      ),
    ],
  }));

  /* And once on the tax page, where the reader is looking at treatments. */
  blocks.push({
    id: "tax.debtTaxedShare",
    section: "tax",
    kind: "callout",
    tone: "caution",
    summary: true,
    weight: 2,
    subject: "plan",
    title: "The cheapest improvement in this document costs nothing to make",
    body: [
      p(
        `Part of every hybrid allocation here carries debt taxation at your ${percentWhole(facts.tax.bracketPct)} slab, while an arbitrage fund of comparable risk gets equity treatment. The planning engine flags this itself in its own taxation note.`
      ),
      p(
        `Switching costs nothing and changes the post-tax outcome for the whole holding period.`
      ),
    ],
  });

  return blocks;
};

/** The offset is an accounting device, and should say so. */
const offsetHonestyBlocks = (
  liability: LiabilityFacts,
  index: number
): Block[] => [
  {
    id: "liability.offsetIsNotASaving",
    section: "liability-funds",
    kind: "callout",
    tone: "neutral",
    explanatory: true,
    subject: "liability",
    subjectId: index,
    subjectName: liability.label,
    title: "The offset does not reduce your interest",
    body: [
      p(
        `You still pay ${money(liability.totalInterest)} to the lender. What this builds is a separate pot that should be worth about the same by the time the loan ends, so the two notionally cancel.`
      ),
      p(
        `It is an accounting device rather than a saving, and it flatters itself slightly: the interest is paid month by month, while the offset pot is only measured at the end.`
      ),
    ],
  },
];

/** The levers, sized. Only where the drawdown does not work. */
const leverBlocks = (facts: PlanFacts): Block[] => {
  const swp = facts.swp;
  if (!swp || !swp.exhausted || !swp.rateValid) return [];
  const goal = facts.goals.find((candidate) => candidate.type === "Retirement");

  return [
    {
      id: "swp.levers",
      section: "what-it-takes",
      kind: "callout",
      tone: "critical",
      subject: "swp",
      title: "Three levers, and this needs more than one of them",
      body: [
        goal
          ? p(
              `Raise the goal. Sustaining this income needs about ${money(swp.sustainableCorpus)} at the drawdown date, which is roughly ${money(swp.sustainableInTodaysMoney)} in today's money — against the ${money(goal.costToday)} the goal is currently set to. That is the honest size of the target.`
            )
          : p(
              `Raise the goal. Sustaining this income needs about ${money(swp.sustainableCorpus)} at the drawdown date. Nothing in this plan is currently sized to build it.`
            ),
        p(
          `Retire later. Every year past the current date adds a year of saving and removes a year of drawing. On a gap this wide it is the most powerful lever by some margin, and the only one that does not require finding money you do not have.`
        ),
        p(
          `Draw less. ${money(swp.net)} a ${periodNoun(swp.frequency)} is what the corpus cannot support. The table above shows what it can.`
        ),
      ],
    },
  ];
};

/** What would actually move a shortfall, and what the plan does not cover. */
const monthlyBlocks = (facts: PlanFacts): Block[] => {
  const blocks: Block[] = [];

  if (facts.affordability === "short") {
    /* Ordering, not arithmetic: this layer may compare figures but must never
       compute one, so the comparators return a sign rather than a difference. */
    const largestBy = <T,>(items: T[], value: (item: T) => number): T | undefined =>
      items.slice().sort((a, b) => (value(b) > value(a) ? 1 : value(b) < value(a) ? -1 : 0))[0];

    const biggestGoal = largestBy(facts.goals, (goal) => goal.monthlySip);
    const biggestLoan = largestBy(facts.liabilities, (liability) => liability.emi);

    blocks.push({
      id: "plan.whatWouldMoveIt",
      section: "monthly",
      kind: "callout",
      tone: "critical",
      weight: 3,
      subject: "plan",
      title: "What would actually move it",
      body: [
        ...(biggestLoan
          ? [
              p(
                `Deal with the loan. The ${money(biggestLoan.emi)} EMI leaves your account before any of this plan begins. Clearing it, refinancing it, or extending its term releases more cash than every other lever here combined, and it is the only one that changes the surplus figure itself.`
              ),
            ]
          : []),
        ...(biggestGoal
          ? [
              p(
                `Re-scope or re-date the largest goal. ${capitalise(biggestGoal.phrase)} asks for ${money(biggestGoal.monthlySip)} of the ${money(facts.committed)} total. Whatever replaces that target should be derived from what you need, not chosen first.`
              ),
            ]
          : []),
        p(
          `Sequence rather than parallel-run. Funding the smallest commitment first is achievable today; starting all of them at once is not, and a plan that is underfunded from day one tends to be abandoned rather than partially followed.`
        ),
      ],
    });
  }

  blocks.push({
    id: "plan.outOfScope",
    section: "monthly",
    kind: "callout",
    tone: "neutral",
    explanatory: true,
    subject: "plan",
    title: "What this plan does not cover",
    body: [
      p(
        `An emergency reserve, life and health cover, and any other assets or liabilities you hold are outside its scope.${facts.affordability === "short" ? " Given how little monthly headroom exists here, confirming that three to six months of expenses are already set aside somewhere liquid matters more than any allocation in this document." : ""}`
      ),
    ],
  });

  return blocks;
};

/** What has to be settled before an order is placed. */
const orderBlocks = (facts: PlanFacts): Block[] => {
  if (!facts.orderCount) return [];
  const items: Fragment[][] = [];

  const callGoal = facts.goals.find((goal) => goal.bond?.isCallDate);
  if (callGoal?.bond) {
    items.push(
      p(
        `Confirm the call terms. Whether the ${dateLong(callGoal.bond.maturity)} redemption is at your option or the issuer's decides whether the principal is dependable. See `,
        { ref: "deployment" },
        `.`
      )
    );
  }

  const payoutGoal = facts.goals.find((goal) => goal.funds.dividendPlans.length);
  if (payoutGoal) {
    items.push(
      p(
        `Switch the payout holding to its growth plan. The payout variant distributes rather than compounds, and the instalment assumes compounding.`
      )
    );
  }

  if (facts.affordability === "short") {
    items.push(
      p(
        `The monthly total is not currently fundable — ${money(facts.committed)} against a ${money(facts.surplus)} surplus. Placing the one-time orders alone commits capital to goals whose monthly leg cannot yet be sustained.`
      )
    );
  }

  if (!items.length) return [];
  return [
    {
      id: "orders.beforePlacing",
      section: "orders",
      kind: "callout",
      tone: "caution",
      subject: "plan",
      title: `${capitalise(words(items.length))} ${items.length === 1 ? "thing" : "things"} to settle before any of this is placed`,
      body: items,
    },
  ];
};

export const buildBlocks = (facts: PlanFacts): Block[] => {
  const blocks: Block[] = [glanceOpening(facts)];
  blocks.push(...lumpsumAllocationBlock(facts));
  facts.goals.forEach((goal) => {
    blocks.push(...goalBlocks(goal, facts));
    blocks.push(...callDateBlock(goal));
    blocks.push(...lumpsumShareBlock(goal));
  });
  blocks.push(...retirementCoherenceBlocks(facts));
  facts.liabilities.forEach((liability, index) => {
    blocks.push(...liabilityBlocks(liability, index, facts));
    if (liability.funds.count > 0) {
      blocks.push(...offsetHonestyBlocks(liability, index));
    }
  });
  blocks.push(...swpBlocks(facts));
  blocks.push(...leverBlocks(facts));
  blocks.push(...taxBlocks(facts));
  blocks.push(...debtTaxBlocks(facts));
  blocks.push(...orderBlocks(facts));

  if (facts.affordability === "unrecorded") {
    blocks.push({
      id: "plan.noSurplus",
      section: "monthly",
      kind: "callout",
      tone: "caution",
      summary: true,
      subject: "plan",
      title: "No monthly surplus is recorded",
      body: [
        p(
          `You have recorded a monthly investable surplus of ${moneyExact(0)}, so this plan cannot be assessed for affordability yet. The instalments below are what the goals require, not what has been shown to be available.`
        ),
      ],
    });
  } else if (facts.affordability === "short") {
    blocks.push({
      id: "plan.shortfall",
      section: "monthly",
      kind: "callout",
      tone: "critical",
      summary: true,
      weight: 5,
      subject: "plan",
      title: `The plan asks for ${money(facts.shortfall)} a month more than your surplus`,
      body: [
        p(
          `Your recorded surplus is ${money(facts.surplus)} and the plan asks for ${money(facts.committed)}. This is not a gap that fund selection or a different bond can close; the inputs themselves need to change.`
        ),
      ],
    });
  }

  /* Last, so the monthly section reads verdict → levers → scope rather than
     opening on what the plan does not cover. */
  blocks.push(...monthlyBlocks(facts));

  return blocks;
};

